import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "./ui/dialog";
import { useLanguage } from "../context/LanguageContext";
import { useSpaData } from "../context/SpaDataContext";
import { openWhatsApp, imgUrl } from "../lib/spa";
import { WhatsAppIcon } from "./WhatsAppIcon";
import { Check } from "lucide-react";

export const BookingModal = ({ state, setOpen }) => {
  const { t, ml } = useLanguage();
  const { therapists, massages } = useSpaData();
  const [therapistSlug, setTherapistSlug] = useState(null);
  const [massageSlug, setMassageSlug] = useState(null);

  useEffect(() => {
    if (state.open) {
      setTherapistSlug(state.therapistSlug);
      setMassageSlug(state.massageSlug);
    }
  }, [state]);

  const therapist = therapists.find((x) => x.slug === therapistSlug);
  const massage = massages.find((x) => x.slug === massageSlug);

  const handleConfirm = () => {
    if (!therapist) return;
    const msg = t("booking.message")(therapist.name, massage ? ml(massage.name) : null);
    openWhatsApp(msg);
    setOpen(false);
  };

  return (
    <Dialog open={state.open} onOpenChange={setOpen}>
      <DialogContent
        data-testid="booking-modal"
        className="max-w-2xl border-foreground/10 bg-card p-0 text-foreground max-h-[90vh] overflow-y-auto"
      >
        <div className="p-6 sm:p-8">
          <p className="text-[10px] uppercase tracking-[0.4em] text-gold">Magnolia Lunar</p>
          <DialogTitle className="mt-2 font-display text-3xl font-medium">{t("booking.title")}</DialogTitle>
          <DialogDescription className="mt-2 text-sm text-foreground/65">{t("booking.subtitle")}</DialogDescription>

          {/* Step 1 — therapist */}
          <p className="mt-8 mb-3 text-xs uppercase tracking-[0.25em] text-foreground/72">
            01 · {t("booking.stepTherapist")}
          </p>
          <div className="grid grid-cols-3 gap-3 sm:grid-cols-6">
            {therapists.map((th) => (
              <button
                key={th.slug}
                data-testid={`booking-therapist-${th.slug}`}
                onClick={() => setTherapistSlug(th.slug)}
                className={`group relative overflow-hidden rounded-xl border transition-colors duration-300 ${
                  therapistSlug === th.slug
                    ? "border-gold"
                    : "border-foreground/10 hover:border-foreground/30"
                }`}
              >
                <div className="aspect-[3/4] overflow-hidden">
                  <img
                    src={imgUrl(th.profile)}
                    alt={th.name}
                    loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                </div>
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/90 to-transparent px-1 pb-1.5 pt-6 text-center">
                  <span className="text-xs font-medium text-white">{th.name}</span>
                </div>
                {therapistSlug === th.slug && (
                  <span className="absolute right-1.5 top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-gold text-black">
                    <Check className="h-3 w-3" strokeWidth={3} />
                  </span>
                )}
              </button>
            ))}
          </div>

          {/* Step 2 — massage */}
          <p className="mt-8 mb-3 text-xs uppercase tracking-[0.25em] text-foreground/72">
            02 · {t("booking.stepMassage")}{" "}
            <span className="normal-case tracking-normal text-foreground/45">({t("booking.optional")})</span>
          </p>
          <div className="flex flex-wrap gap-2">
            {massages.map((m) => (
              <button
                key={m.slug}
                data-testid={`booking-massage-${m.slug}`}
                onClick={() => setMassageSlug(massageSlug === m.slug ? null : m.slug)}
                className={`rounded-full border px-4 py-2 text-sm transition-colors duration-300 ${
                  massageSlug === m.slug
                    ? "border-gold bg-gold/10 text-gold"
                    : "border-foreground/15 text-foreground/82 hover:border-foreground/40 hover:text-foreground"
                }`}
              >
                {ml(m.name)}
              </button>
            ))}
          </div>

          <button
            data-testid="booking-confirm-button"
            onClick={handleConfirm}
            disabled={!therapist}
            className="mt-8 flex w-full items-center justify-center gap-3 rounded-full bg-gold px-8 py-4 text-sm font-semibold uppercase tracking-[0.2em] text-black transition-all duration-300 hover:bg-gold-light disabled:cursor-not-allowed disabled:opacity-30"
          >
            <WhatsAppIcon className="h-5 w-5" />
            {therapist ? t("booking.openWhatsApp") : t("booking.selectFirst")}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
